import config from "environment";
import apiClient, { downloadRequest, downloadRequestPost, getRequest, postRequest } from "./client";
import { 
  CopyFormPayload,
  DownloadFormResponsesSelectedPayload,
  FormExtended,
  GetHiddenQuestionsForFormAndUserPayload,
  RenameFormPayload,
  UpdateFormChangesPayload,
  UpdateFormNamePayload,
  UpdateFormPublicLinkPayload,
  UpdateFormPublishedVersionIdContract,
  UpdateFormPublishedVersionIdPayload,
  UpdateFormQuestionOrderPayload,
  UpdateFormSettingsPayload,
  UpdateFormTagsPayload,
  UpdateFormThemeForFormPayload, 
  UpdateHiddenQuestionsPayload
} from "effno-core";

const { baseApi } = config;

export async function apiGetForm(id: string) : Promise<FormExtended | null> {
  const fetchURL = `${baseApi}/forms/${id}`;
  return await getRequest(fetchURL, "API/apiGetForm");
}

export async function apiGetFormFromPublicLink(publicLink: string) : Promise<FormExtended | null> {
  const fetchURL = `${baseApi}/forms/public-link/${publicLink}`;
  return await getRequest(fetchURL, "API/apiGetFormFromPublicLink");
}

export async function apiGetEditableFormsForWorkspace(workspaceId: string) : Promise<FormExtended[] | null> {
  const fetchURL = `${baseApi}/forms/editable/workspace/${workspaceId}`;
  return await getRequest(fetchURL, "API/apiGetEditableFormsForWorkspace");
}

export async function apiCreateFormInWorkspace(workspaceId: string) : Promise<FormExtended | null> {
  const fetchURL = `${baseApi}/forms/create/${workspaceId}`;
  return await getRequest(fetchURL, "API/apiCreateFormInWorkspace");
}

export async function apiDeleteForm(formId: string) : Promise<FormExtended | null> {
  const fetchURL = `${baseApi}/forms/delete/${formId}`;
  return await getRequest(fetchURL, "API/apiDeleteForm");
}

export async function apiUpdateFormQuestionOrder(payload: UpdateFormQuestionOrderPayload) 
: Promise<FormExtended | null> {
  const fetchURL = `${baseApi}/forms/update-order`;
  return await postRequest(fetchURL, payload, "API/apiUpdateFormQuestionOrder");
}

export async function apiRenameForm(payload: RenameFormPayload) 
: Promise<FormExtended | null> {
  const fetchURL = `${baseApi}/forms/rename`;
  return await postRequest(fetchURL, payload, "API/apiRenameForm");
}

export async function apiCopyForm(payload: CopyFormPayload) 
: Promise<FormExtended | null> {
  const fetchURL = `${baseApi}/forms/copy`;
  return await postRequest(fetchURL, payload, "API/apiCopyForm");
}
export async function apiUpdateFormSettings(payload: UpdateFormSettingsPayload) 
: Promise<FormExtended | null> {
  const fetchURL = `${baseApi}/forms/update-settings`;
  return await postRequest(fetchURL, payload, "API/apiUpdateFormSettings");
}

export async function apiUpdateFormName(payload: UpdateFormNamePayload) 
: Promise<FormExtended | null> {
  const fetchURL = `${baseApi}/forms/update-name`;
  return await postRequest(fetchURL, payload, "API/apiUpdateFormName");
}

export async function apiDownloadFormResponses(id: string) : Promise<any | null> {
  const fetchURL = `${baseApi}/forms/download/${id}`;
  return await downloadRequest(fetchURL, "API/apiDownloadFormResponses");
}

export async function apiDownloadFormResponsesSelected(payload: DownloadFormResponsesSelectedPayload) 
: Promise<any | null> {
  const fetchURL = `${baseApi}/forms/download-selected`;
  return await downloadRequestPost(fetchURL, payload, "API/apiDownloadFormResponsesSelected");
}

export async function apiCreateFormPublicLink(formId: string) : Promise<FormExtended | null> {
  const fetchURL = `${baseApi}/forms/create-public-link/${formId}`;
  return await getRequest(fetchURL, "API/apiCreateFormPublicLink");
}

export async function apiUpdateFormPublicLink(payload: UpdateFormPublicLinkPayload) 
: Promise<FormExtended | null> {
  const fetchURL = `${baseApi}/forms/update-public-link`;
  return await postRequest(fetchURL, payload, "API/apiUpdateFormPublicLink");
}

export async function apiUpdateFormPublishedVersionId(payload: UpdateFormPublishedVersionIdPayload) 
: Promise<FormExtended | null> {
  const fetchURL = `${baseApi}/forms/update-published-version-id`;
  return await postRequest(fetchURL, payload, "API/apiUpdateFormPublishedVersionId");
}

export async function apiFormHasEdits(formId: string) : Promise<FormExtended | null> {
  const fetchURL = `${baseApi}/forms/has-edits/${formId}`;
  return await getRequest(fetchURL, "API/apiFormHasEdits");
}

export async function apiFormHasNoEdits(formId: string) : Promise<FormExtended | null> {
  const fetchURL = `${baseApi}/forms/has-no-edits/${formId}`;
  return await getRequest(fetchURL, "API/apiFormHasNoEdits");
}

export async function apiUpdateFormTags(payload: UpdateFormTagsPayload) 
: Promise<FormExtended | null> {
  const fetchURL = `${baseApi}/forms/update-tags`;
  return await postRequest(fetchURL, payload, "API/apiUpdateFormTags");
}

export async function apiUpdateFormChanges(payload: UpdateFormChangesPayload) 
: Promise<FormExtended | null> {
  const fetchURL = `${baseApi}/forms/update-changes`;
  return await postRequest(fetchURL, payload, "API/apiUpdateFormChanges");
}

export async function apiUpdateFormThemeForForm(payload: UpdateFormThemeForFormPayload) 
: Promise<FormExtended | null> {
  const fetchURL = `${baseApi}/forms/update-form-theme`;
  return await postRequest(fetchURL, payload, "API/apiUpdateFormThemeForForm");
}

export async function apiGetHiddenQuestionsForFormAndUser(payload: GetHiddenQuestionsForFormAndUserPayload) 
: Promise<string[] | null> {
  const fetchURL = `${baseApi}/forms/hidden-questions`;
  return await postRequest(fetchURL, payload, "API/apiGetHiddenQuestionsForFormAndUser");
}

export async function apiUpdateHiddenQuestions(payload: UpdateHiddenQuestionsPayload) 
: Promise<string[] | null> {
  const fetchURL = `${baseApi}/forms/update-hidden-questions`;
  return await postRequest(fetchURL, payload, "API/apiUpdateHiddenQuestionsForFormAndUser");
}

// finds the editing form which has the original name
export async function apiGetFormNameFromPublishedId(publishedId: string) : Promise<string | null> {
  const fetchURL = `${baseApi}/forms/editing-form-name/${publishedId}`;
  return await getRequest(fetchURL, "API/apiGetFormNameFromPublishedId");
}

// finds the parent for from the id of the published version
export async function apiGetFormParent(publishedVersionId: string) : Promise<FormExtended | null> {
  const fetchURL = `${baseApi}/forms/parent/${publishedVersionId}`;
  return await getRequest(fetchURL, "API/apiGetFormParent");
}

